import { Button, Input, Select, Space, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as Component from '../../../shared/components';
import { TableColumnInterface } from '../../../shared/type/tableType';
import { burnInService } from '../services/burnInService';
import BurnInStatusTag from '../components/BurnInStatusTag';
import { BurnInJob, BurnInJobStatus } from '../types/burnInTypes';

const BurnInDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '20',
    ordering: '-created',
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [_selectedList, setSelectedList] = useState<number[]>([]);
  const [summary, setSummary] = useState({ total: 0, completed: 0, processing: 0, pending: 0, failed: 0 });

  // Filter states
  const [status, setStatus] = useState<string>(searchParams.get('status') || '');
  const [lang, setLang] = useState<string>(searchParams.get('lang') || '');
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    if (status) {
      newParams.set('status', status);
    } else {
      newParams.delete('status');
    }

    if (lang) {
      newParams.set('lang', lang);
    } else {
      newParams.delete('lang');
    }

    if (searchQuery) {
      newParams.set('search', searchQuery);
    } else {
      newParams.delete('search');
    }

    newParams.set('page', '1');
    setSearchParams(newParams);
  }, [status, lang, searchQuery]);

  const columns: TableColumnInterface<BurnInJob>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '5%',
      sorter: true,
    },
    {
      title: 'Content Title',
      dataIndex: 'source_content_title',
      key: 'source_content_title',
      width: '20%',
      render: (_text: string, record: BurnInJob): React.ReactNode => (
        <Link
          to={`/episode/edit/${record.source_content_id}`}
          style={{ color: '#1890ff', fontWeight: 500 }}
        >
          {record.source_content_title || `Content #${record.source_content_id}`}
        </Link>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'lang',
      key: 'lang',
      width: '8%',
      render: (text: string): React.ReactNode => (
        <span style={{ textTransform: 'uppercase', fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (_text: string, record: BurnInJob): React.ReactNode => (
        <BurnInStatusTag status={record.status} />
      ),
    },
    {
      title: 'Batch Group',
      dataIndex: 'batch_group_id',
      key: 'batch_group_id',
      width: '12%',
      render: (text: string): React.ReactNode => {
        if (!text) return <span style={{ color: '#999' }}>-</span>;
        return (
          <Tooltip title={text}>
            <span style={{ fontSize: '12px' }}>{text.substring(0, 8)}...</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: '10%',
      sorter: true,
      render: (text: string): React.ReactNode => (
        <span>{new Date(text).toLocaleDateString()}</span>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: '12%',
      render: (_text: string, record: BurnInJob): React.ReactNode => (
        <Space size="small">
          <Link to={`/assets/edit/${record.source_video_id}`} style={{ fontSize: '12px' }}>
            Source Video
          </Link>
          {record.status === 'completed' && record.result_content_id && (
            <Link
              to={`/episode/edit/${record.result_content_id}`}
              style={{ fontSize: '12px', color: '#52c41a' }}
            >
              Result
            </Link>
          )}
        </Space>
      ),
    },
  ];

  const fetchData = useCallback(
    async (params: URLSearchParams): Promise<{ data: BurnInJob[]; count: number }> => {
      try {
        const result = await burnInService.fetch(params);
        // Compute summary from full count (server-side)
        // We track the total from current fetch; status-specific counts are approximate from current page
        const jobs = result.results;
        setSummary({
          total: result.count,
          completed: jobs.filter(j => j.status === 'completed').length,
          processing: jobs.filter(j => j.status === 'processing').length,
          pending: jobs.filter(j => j.status === 'pending').length,
          failed: jobs.filter(j => j.status === 'failed').length,
        });
        return { data: jobs, count: result.count };
      } catch (error) {
        console.error('Failed to fetch burn-in jobs:', error);
        return { data: [], count: 0 };
      }
    },
    []
  );

  useEffect(() => {
    setRefreshCounter(prev => prev + 1);
  }, [searchParams]);

  return (
    <Component.BasePageComponent>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Tooltip title="Search by content title or slug">
            <Input.Search
              placeholder="Search content title..."
              allowClear
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onSearch={value => setSearchQuery(value)}
              style={{ width: 250 }}
              enterButton
            />
          </Tooltip>

          <Select
            style={{ width: 160 }}
            placeholder="Status: All"
            allowClear
            value={status || undefined}
            onChange={value => setStatus(value || '')}
          >
            <Select.Option value="pending">Pending</Select.Option>
            <Select.Option value="processing">Processing</Select.Option>
            <Select.Option value="completed">Completed</Select.Option>
            <Select.Option value="failed">Failed</Select.Option>
          </Select>

          <Select
            style={{ width: 140 }}
            placeholder="Language: All"
            allowClear
            value={lang || undefined}
            onChange={value => setLang(value || '')}
          >
            <Select.Option value="ko">Korean</Select.Option>
            <Select.Option value="en">English</Select.Option>
            <Select.Option value="es">Spanish</Select.Option>
            <Select.Option value="zh">Chinese</Select.Option>
          </Select>
        </div>

        <Link to="/burn-in/create">
          <Button type="primary">New Request</Button>
        </Link>
      </div>

      <Component.TableComponent<BurnInJob>
        key={refreshCounter}
        name="Subtitle Burn-in Jobs"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchData}
        columns={columns}
        options={{
          isDisableIdColumn: true,
        }}
        actions={{
          onSelectionChange: setSelectedList,
        }}
      />

      <div style={{ marginTop: 16, padding: '8px 16px', background: '#fafafa', borderRadius: 4, fontSize: '13px' }}>
        <Space size="large">
          <span>Total: <strong>{summary.total}</strong></span>
          <span style={{ color: '#52c41a' }}>Completed: <strong>{summary.completed}</strong></span>
          <span style={{ color: '#1890ff' }}>Processing: <strong>{summary.processing}</strong></span>
          <span>Pending: <strong>{summary.pending}</strong></span>
          <span style={{ color: '#ff4d4f' }}>Failed: <strong>{summary.failed}</strong></span>
        </Space>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(BurnInDashboard);
